import {
  AI_DATA_POLICY_CHANGES_PAGE_SIZE,
  type AiDataPolicy,
  type AiDataPolicyChange,
  type AiDataPolicyService,
  type AiDataPolicyWorkspace,
  type PolicyActor,
} from './types';

export interface MemoryAiDataPolicyOptions {
  policy?: AiDataPolicy;
  changes?: AiDataPolicyChange[];
  actor?: PolicyActor;
  workspace?: AiDataPolicyWorkspace;
  now?: () => Date;
}

export interface MemoryAiDataPolicyService extends AiDataPolicyService {
  current(): AiDataPolicy;
  push(policy: AiDataPolicy, notify?: boolean): void;
}

export const NOT_CHOSEN: AiDataPolicy = { value: 'not_chosen', setBy: null, setAt: null };

export function createMemoryAiDataPolicyService(
  options: MemoryAiDataPolicyOptions = {},
): MemoryAiDataPolicyService {
  let policy: AiDataPolicy = options.policy ?? NOT_CHOSEN;
  let changes: AiDataPolicyChange[] = [...(options.changes ?? [])];
  let seq = changes.length;
  const actor: PolicyActor = options.actor ?? { id: 'u-admin', name: 'Lisa K.' };
  const now = options.now ?? (() => new Date());
  const listeners = new Set<(next: AiDataPolicy) => void>();

  return {
    current: () => policy,
    push(next, notify = true) {
      policy = next;
      if (notify) for (const listener of listeners) listener(next);
    },
    getPolicy: () => Promise.resolve(policy),
    setPolicy({ value, onlyIfUndecided }) {
      if (policy.value === value || (onlyIfUndecided && policy.value !== 'not_chosen')) {
        return Promise.resolve({ changed: false, policy });
      }
      const at = now().toISOString();
      changes = [
        { id: `change-${String(++seq)}`, fromValue: policy.value, toValue: value, createdAt: at },
        ...changes,
      ];
      policy = { value, setBy: actor, setAt: at };
      return Promise.resolve({ changed: true, policy });
    },
    listChanges(page) {
      const size = AI_DATA_POLICY_CHANGES_PAGE_SIZE;
      const start = (page - 1) * size;
      return Promise.resolve({
        items: changes.slice(start, start + size),
        total: changes.length,
        page,
        hasNextPage: start + size < changes.length,
      });
    },
    getWorkspace: () => Promise.resolve(options.workspace ?? { peopleCount: null, admins: null }),
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
